import { Navigate, useParams } from 'react-router-dom';

import {
  CONFIGURATOR_SLOT_IDS,
  CONFIGURATOR_SLOT_LABELS_RU,
  CONFIGURATOR_VIDEO_URL,
  FURNITURE_TYPE_LABELS_RU,
  FurnitureTypeSchema,
  type ConfiguratorSlotId,
  type FurnitureType,
} from '@pomoshnik/shared';

import { BottomBar } from '@/components/BottomBar/BottomBar';
import { Header } from '@/components/Header/Header';
import { VideoButton } from '@/components/VideoButton';
import { useConfiguratorStore } from '@/lib/stores/configuratorStore';

import { ConfiguratorVisualization } from './ConfiguratorVisualization';
import { resolveSlotColor } from './slotResolver';

/**
 * Экран 2.6.2 — конфигуратор мебели (Block 6 Phase 2, ADR-0015 §«Phase 2»).
 *
 * Phase 2 расширил Phase 1 stub до полноценного экрана 2.6.2:
 *   - VideoButton (entry-screen function — CLAUDE.md §16.4 / B-008)
 *   - ConfiguratorVisualization (SVG per `FurnitureType`) + легенда
 *   - 5 slot buttons (vertical P-019) + empty-state hint
 *   - «Собрать» (disabled until `isComplete()`) + «Сбросить» (window.confirm)
 *
 * URL guard preserved from Phase 1: невалидный `:type` → /configurator.
 * Header per Q7 — просто `label` (без префикса «Сборка изделия —»).
 *
 * Slot click — alert placeholder (Phase 3 заменит на navigate Block 2/4
 * с `?return=configurator&slot={id}` per ADR-0016).
 *
 * `setType(type)` после `resetAll()` — restore type из URL (resetAll обнуляет
 * type=null, нам нужно сохранить его раз пользователь на /configurator/:type).
 */
export function ConfiguratorScreen() {
  // React Hooks rules: ВСЕ hooks вызываются безусловно ДО любого early return.
  const params = useParams<{ type: string }>();
  const slots = useConfiguratorStore((s) => s.slots);
  const isComplete = useConfiguratorStore((s) => s.isComplete);
  const resetAll = useConfiguratorStore((s) => s.resetAll);
  const setType = useConfiguratorStore((s) => s.setType);

  const parsed = FurnitureTypeSchema.safeParse(params.type);
  if (!parsed.success) return <Navigate to="/configurator" replace />;

  const type: FurnitureType = parsed.data;
  const label = FURNITURE_TYPE_LABELS_RU[type];

  const slotColor = (id: ConfiguratorSlotId): string =>
    resolveSlotColor(id, slots.find((s) => s.id === id)?.selectedItemId ?? null);

  const facadeColor = slotColor('facade');
  const countertopColor = slotColor('countertop');
  const corpusColor = slotColor('corpus');
  const handlesColor = slotColor('handles');
  const hooksColor = slotColor('hooks');

  // Q9 spec: «Скрывается когда все 4 required + optional hooks selected
  // (isComplete() + hooks ≠ null)». Эквивалент `slots.some(s => s.id === null)`
  // — true пока ХОТЬ ОДИН slot пуст (включая optional hooks). Это намеренно
  // НЕ совпадает с `!isComplete()`: «Собрать» становится enabled когда 4
  // required filled, но hint остаётся пока hooks не выбран — поощряет
  // полный выбор без блокировки UX.
  const showHint = slots.some((s) => s.selectedItemId === null);

  const handleReset = () => {
    if (window.confirm('Сбросить выбор?')) {
      resetAll();
      setType(type);
    }
  };

  const handleSlotClick = (slotId: ConfiguratorSlotId) => {
    alert(`Phase 3: /decors/${slotId}?return=configurator&slot=${slotId}`);
  };

  const handleAssemble = () => {
    alert('Phase 4: переход на /configurator/:type/result');
  };

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title={label} />
      <div className="flex-1 flex flex-col gap-4 p-4 overflow-y-auto">
        <VideoButton videoUrl={CONFIGURATOR_VIDEO_URL} />

        <div
          data-testid="configurator-visualization-card"
          className="bg-card border-[1.5px] border-accent rounded-xl shadow-card overflow-hidden p-2"
        >
          <ConfiguratorVisualization
            type={type}
            facadeColor={facadeColor}
            countertopColor={countertopColor}
            corpusColor={corpusColor}
            handlesColor={handlesColor}
            hooksColor={hooksColor}
          />
          <div className="flex gap-3 px-2 py-2 flex-wrap">
            <LegendItem label="1 — Фасад" color={facadeColor} />
            <LegendItem label="2 — Корпус" color={corpusColor} />
            <LegendItem label="3 — Столешница" color={countertopColor} />
          </div>
        </div>

        {showHint && <p className="text-muted text-center text-base">Выбери материал и цвет</p>}

        <div className="flex flex-col gap-2">
          {CONFIGURATOR_SLOT_IDS.map((slotId) => {
            const slot = slots.find((s) => s.id === slotId);
            const slotLabel = CONFIGURATOR_SLOT_LABELS_RU[slotId];
            const selected = slot?.selectedItemId ?? null;
            return (
              <button
                key={slotId}
                type="button"
                onClick={() => handleSlotClick(slotId)}
                aria-label={`Выбрать ${slotLabel}, ${selected ? 'выбрано' : 'не выбрано'}`}
                className={
                  'w-full text-left rounded-xl bg-card border-[1.5px] border-accent shadow-card ' +
                  'p-4 flex items-center gap-3 min-h-tap-chip-lg ' +
                  'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80'
                }
              >
                <span className="font-bold text-ink leading-snug" style={{ fontSize: 20 }}>
                  {slotLabel}
                </span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          disabled={!isComplete()}
          onClick={handleAssemble}
          aria-label="Собрать конфигурацию изделия"
          className={
            'w-full min-h-tap-chip-lg rounded-xl bg-cta text-ink font-bold ' +
            'disabled:opacity-50 disabled:cursor-not-allowed ' +
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90'
          }
          style={{ fontSize: 22 }}
        >
          Собрать
        </button>
        <button
          type="button"
          onClick={handleReset}
          aria-label="Сбросить все выбранные материалы"
          className={
            'w-full min-h-tap-chip rounded-xl bg-transparent border-[1.5px] border-accent ' +
            'text-ink font-medium ' +
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80'
          }
          style={{ fontSize: 18 }}
        >
          Сбросить
        </button>
      </div>
      <BottomBar
        variant="default"
        onFeedback={() => alert('Block 10: открыть форму «Что улучшить?»')}
      />
    </main>
  );
}

interface LegendItemProps {
  label: string;
  color: string;
}

function LegendItem({ label, color }: LegendItemProps) {
  return (
    <div className="flex items-center gap-1">
      <span
        aria-hidden="true"
        className="w-3 h-3 rounded-sm border border-icon-border shrink-0"
        style={{ background: color }}
      />
      <span className="text-separator text-sm">{label}</span>
    </div>
  );
}
